import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PiWarningCircleLight, PiCalendarCheckLight, PiCheckCircleLight, PiClipboardTextLight, PiCopyLight, PiArrowSquareOutLight, PiCircleNotchLight, PiEnvelopeSimpleLight, PiMapPinLight, PiChatCircleLight, PiPlusLight, PiMagnifyingGlassLight, PiUsersLight, PiVideoCameraLight, PiXCircleLight } from 'react-icons/pi';
import { interviewAPI } from '../../services/api';
import SkeletonBlock from '../../components/common/SkeletonBlock';

const inputClass = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';
const textareaClass = 'min-h-[96px] w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-6 text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatDateTime = (value) => {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not scheduled';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const formatTime = (value) => {
  if (!value) return 'No time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No time';
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
};

const extractError = (error, fallback) => (
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const asArray = (value) => Array.isArray(value) ? value : [];

const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['confirmed', 'completed'].includes(normalized)) return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  if (['scheduled', 'candidate_confirmed', 'employer_confirmed'].includes(normalized)) return 'border-sky-100 bg-sky-50 text-sky-700';
  if (['reschedule_requested'].includes(normalized)) return 'border-amber-100 bg-amber-50 text-amber-700';
  if (['cancelled', 'candidate_declined', 'employer_cancelled', 'no_show'].includes(normalized)) return 'border-rose-100 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const initialsFor = (name) => (
  String(name || 'Candidate')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
);

const metricTone = {
  sky: 'text-sky-600',
  slate: 'text-slate-800',
  green: 'text-emerald-600',
  amber: 'text-amber-600'
};

const Metric = ({ icon: Icon, label, value, detail, tone = 'sky' }) => (
  <div className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
    <div className="mb-4 flex items-center justify-between gap-3">
      <span className={`flex items-center justify-center ${metricTone[tone] || metricTone.sky}`}>
        <Icon size={24} />
      </span>
      <span className="text-xs font-medium text-slate-400">{detail}</span>
    </div>
    <div className="font-display text-2xl font-normal leading-none text-[#0b1f3a]">{value}</div>
    <div className="mt-2 text-sm font-medium text-slate-700">{label}</div>
  </div>
);

const InterviewsOverviewSkeleton = () => (
  <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-3">
          <SkeletonBlock className="h-3 w-20 rounded-md" />
          <SkeletonBlock className="h-9 w-64 rounded-md" />
          <SkeletonBlock className="h-4 w-[34rem] max-w-full rounded-md" />
        </div>
        <SkeletonBlock className="h-10 w-28 rounded-lg" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <SkeletonBlock className="h-9 w-9 rounded-lg" />
              <SkeletonBlock className="h-3 w-20 rounded-md" />
            </div>
            <SkeletonBlock className="h-8 w-20 rounded-md" />
            <SkeletonBlock className="mt-2 h-4 w-24 rounded-md" />
          </div>
        ))}
      </div>

      <section className="rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex gap-2">
            {Array.from({ length: 4 }).map((_, index) => <SkeletonBlock key={index} className="h-8 w-24 rounded-md" />)}
          </div>
          <div className="flex flex-1 flex-col gap-3 sm:flex-row xl:max-w-xl">
            <SkeletonBlock className="h-10 flex-1 rounded-lg" />
            <SkeletonBlock className="h-10 w-full rounded-lg sm:w-36" />
          </div>
        </div>

        <div className="px-4 py-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="grid min-w-[1120px] grid-cols-[1.6fr_1.5fr_1.2fr_1fr_0.9fr_1fr] gap-4 border-b border-slate-100 py-4 last:border-0">
              <div className="space-y-2">
                <SkeletonBlock className="h-4 w-36 rounded-md" />
                <SkeletonBlock className="h-3 w-44 rounded-md" />
              </div>
              <div className="space-y-2">
                <SkeletonBlock className="h-4 w-32 rounded-md" />
                <SkeletonBlock className="h-3 w-24 rounded-md" />
              </div>
              <SkeletonBlock className="h-4 w-28 rounded-md" />
              <SkeletonBlock className="h-4 w-20 rounded-md" />
              <SkeletonBlock className="h-7 w-24 rounded-md" />
              <div className="flex justify-end gap-1.5">
                {Array.from({ length: 4 }).map((__, buttonIndex) => <SkeletonBlock key={buttonIndex} className="h-8 w-8 rounded-md" />)}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  </div>
);

const meetingHref = (link) => {
  if (!link) return '';
  if (/^https?:\/\//i.test(link)) return link;
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(link)) return `mailto:${link}`;
  if (/^\+?[0-9 ().-]+$/.test(link)) return `tel:${link}`;
  return `https://${link}`;
};

const EmptyState = ({ title, copy }) => (
  <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white px-6 text-center">
    <div className="mb-4 flex items-center justify-center text-sky-600">
      <PiCalendarCheckLight size={26} />
    </div>
    <h2 className="font-display text-xl font-normal text-[#0b1f3a]">{title}</h2>
    <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">{copy}</p>
  </div>
);

const FeedbackModal = ({ interview, onClose, onSaved }) => {
  const [form, setForm] = useState({
    recommendation: 'proceed',
    outcome: 'yes',
    overallRating: 7,
    technicalSkills: 7,
    communication: 7,
    cultureFit: 7,
    strengths: '',
    concerns: '',
    notes: '',
    candidateMessage: '',
    nextSteps: ''
  });
  const [saving, setSaving] = useState(false);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        notes: form.notes,
        outcome: form.outcome,
        recommendation: form.recommendation,
        overallRating: Number(form.overallRating),
        systemFeedback: {
          technicalSkills: Number(form.technicalSkills),
          communication: Number(form.communication),
          cultureFit: Number(form.cultureFit),
          overallRating: Number(form.overallRating),
          strengths: form.strengths.split('\n').map((item) => item.trim()).filter(Boolean),
          concerns: form.concerns.split('\n').map((item) => item.trim()).filter(Boolean),
          evidenceNotes: form.notes
        },
        candidateFeedback: {
          message: form.candidateMessage,
          nextSteps: form.nextSteps
        }
      };

      if (interview.job?.id && interview.match?.id) {
        await interviewAPI.saveMatchFeedback(interview.job.id, interview.match.id, interview.id, payload);
      } else {
        await interviewAPI.saveFeedback(interview.id, {
          notes: form.notes,
          recommendation: form.recommendation,
          feedback: payload.systemFeedback
        });
      }

      toast.success('Interview feedback saved.');
      onSaved();
      onClose();
    } catch (error) {
      toast.error(extractError(error, 'Failed to save feedback'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-3 sm:p-6">
      <form onSubmit={handleSubmit} className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.20)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-4 sm:px-5">
          <div>
            <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Interview Feedback</h2>
            <p className="mt-1 text-sm text-slate-500">{interview.candidate?.name} · {interview.job?.title}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100">
            <PiXCircleLight size={14} />
          </button>
        </div>

        <div className="grid gap-4 p-4 sm:p-5 md:grid-cols-2">
          <label>
            <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Recommendation</span>
            <select value={form.recommendation} onChange={(event) => update('recommendation', event.target.value)} className={`${inputClass} w-full`}>
              <option value="proceed">Proceed</option>
              <option value="needs_discussion">Needs discussion</option>
              <option value="reject">Reject</option>
            </select>
          </label>
          <label>
            <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Outcome</span>
            <select value={form.outcome} onChange={(event) => update('outcome', event.target.value)} className={`${inputClass} w-full`}>
              <option value="strong_yes">Strong yes</option>
              <option value="yes">Yes</option>
              <option value="hold">Hold</option>
              <option value="no">No</option>
              <option value="no_show">No show</option>
              <option value="needs_discussion">Needs discussion</option>
            </select>
          </label>
          {[
            ['overallRating', 'Overall rating'],
            ['technicalSkills', 'Technical skills'],
            ['communication', 'Communication'],
            ['cultureFit', 'Culture fit']
          ].map(([field, label]) => (
            <label key={field}>
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">{label}</span>
              <input type="number" min="1" max="10" value={form[field]} onChange={(event) => update(field, event.target.value)} className={`${inputClass} w-full`} />
            </label>
          ))}
          <label className="md:col-span-2">
            <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Internal notes</span>
            <textarea value={form.notes} onChange={(event) => update('notes', event.target.value)} className={textareaClass} />
          </label>
          <label>
            <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Strengths</span>
            <textarea value={form.strengths} onChange={(event) => update('strengths', event.target.value)} className={textareaClass} />
          </label>
          <label>
            <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Concerns</span>
            <textarea value={form.concerns} onChange={(event) => update('concerns', event.target.value)} className={textareaClass} />
          </label>
          <label>
            <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Candidate message</span>
            <textarea value={form.candidateMessage} onChange={(event) => update('candidateMessage', event.target.value)} className={textareaClass} />
          </label>
          <label>
            <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Next steps</span>
            <textarea value={form.nextSteps} onChange={(event) => update('nextSteps', event.target.value)} className={textareaClass} />
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-4 sm:px-5">
          <button type="button" onClick={onClose} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:opacity-60">
            {saving ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiClipboardTextLight size={13} />}
            Save feedback
          </button>
        </div>
      </form>
    </div>
  );
};

const InterviewRow = ({ interview, onJoin, onCopy, onConfirm, onCancel, onFeedback, busy }) => {
  const status = interview.status;
  const candidateName = interview.candidate?.name || 'Candidate';
  const isClosed = ['completed', 'cancelled', 'candidate_declined', 'employer_cancelled', 'no_show'].includes(String(status).toLowerCase());
  const interviewers = asArray(interview.interviewerEmails);
  const confirmations = [
    interview.employerConfirmed ? 'Employer confirmed' : 'Employer pending',
    interview.candidateConfirmed ? 'Candidate confirmed' : 'Candidate pending'
  ].join(' · ');
  const needsFeedback = status === 'completed' && !interview.resultStatus && !interview.notes;

  return (
    <tr className="border-b border-slate-100 last:border-0">
      <td className="py-4 pr-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#eef9ff] text-xs font-medium text-sky-700">
            {initialsFor(candidateName)}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-[#0b1f3a]">{candidateName}</div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
              {interview.candidate?.email && <span className="inline-flex items-center gap-1"><PiEnvelopeSimpleLight size={12} /> {interview.candidate.email}</span>}
              {interview.candidate?.location && <span className="inline-flex items-center gap-1"><PiMapPinLight size={12} /> {interview.candidate.location}</span>}
              {interview.match?.score !== null && interview.match?.score !== undefined && <span>Match {Math.round(Number(interview.match.score || 0))}%</span>}
            </div>
          </div>
        </div>
      </td>
      <td className="py-4 pr-4">
        <div className="max-w-[300px]">
          <div className="truncate text-sm font-medium text-[#0b1f3a]">{interview.title || 'Interview'}</div>
          <div className="mt-1 text-xs text-slate-500">{interview.job?.title || 'No job'} · {labelize(interview.type)}</div>
          {interview.resultStatus && <div className="mt-1 text-xs text-slate-500">Result: {labelize(interview.resultStatus)}</div>}
        </div>
      </td>
      <td className="py-4 pr-4">
        <div className="text-sm font-medium text-[#0b1f3a]">{formatTime(interview.scheduledDate)}</div>
        <div className="mt-1 text-xs text-slate-500">{formatDateTime(interview.scheduledDate)}</div>
      </td>
      <td className="py-4 pr-4">
        <div className="text-sm text-slate-600">{interview.platform || labelize(interview.type)}</div>
        <div className="mt-1 text-xs text-slate-500">{interview.durationMinutes || interview.duration || 60} min · {interview.timezone || 'UTC'}</div>
        <div className="mt-1 text-xs text-slate-500">{interview.location || `${interviewers.length} interviewer${interviewers.length === 1 ? '' : 's'}`}</div>
      </td>
      <td className="py-4 pr-4">
        <span className={`inline-flex rounded-md border px-2.5 py-1 text-xs font-medium ${statusTone(status)}`}>
          {labelize(status)}
        </span>
        <div className="mt-1 max-w-[220px] text-xs leading-5 text-slate-500">{confirmations}</div>
      </td>
      <td className="py-4 text-right">
        <div className="flex justify-end gap-1.5">
          {interview.meetingLink && (
            <>
              <button onClick={() => onJoin(interview)} className="flex h-8 w-8 items-center justify-center rounded-md bg-sky-600 text-white transition hover:bg-sky-700 btn-premium-base btn-premium-highlight-sky" title="Open meeting">
                <PiArrowSquareOutLight size={12} />
              </button>
              <button onClick={() => onCopy(interview)} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700" title="Copy meeting link">
                <PiCopyLight size={12} />
              </button>
            </>
          )}
          <button 
            onClick={() => onConfirm(interview)} 
            disabled={busy || isClosed || interview.employerConfirmed} 
            className={
              !isClosed && !interview.employerConfirmed
                ? "flex h-8 w-8 items-center justify-center rounded-md bg-emerald-600 text-white transition hover:bg-emerald-700 disabled:opacity-40 btn-premium-base btn-premium-highlight-emerald"
                : "flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-40"
            }
            title="Confirm"
          >
            {busy === 'confirm' ? <PiCircleNotchLight size={12} className="animate-spin" /> : <PiCheckCircleLight size={12} />}
          </button>
          <button 
            onClick={() => onFeedback(interview)} 
            disabled={busy} 
            className={
              needsFeedback
                ? "flex h-8 w-8 items-center justify-center rounded-md bg-amber-500 text-white transition hover:bg-amber-600 disabled:opacity-40 btn-premium-base btn-premium-highlight-amber"
                : "flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 disabled:opacity-40"
            }
            title="Feedback"
          >
            <PiChatCircleLight size={12} />
          </button>
          <button onClick={() => onCancel(interview)} disabled={busy || isClosed} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40" title="Cancel">
            {busy === 'cancel' ? <PiCircleNotchLight size={12} className="animate-spin" /> : <PiXCircleLight size={12} />}
          </button>
        </div>
      </td>
    </tr>
  );
};

const InterviewsOverview = () => {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('upcoming');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [feedbackInterview, setFeedbackInterview] = useState(null);
  const [isHighlighting, setIsHighlighting] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsHighlighting(false), 3500);
    return () => clearTimeout(timer);
  }, []);

  const loadInterviews = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await interviewAPI.getAll({ limit: 100, offset: 0 });
      setInterviews(Array.isArray(response.interviews) ? response.interviews : []);
    } catch (loadError) {
      setError(extractError(loadError, 'Failed to load interviews'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInterviews();
  }, [loadInterviews]);

  const now = Date.now();
  const metrics = useMemo(() => {
    const upcoming = interviews.filter((item) => new Date(item.scheduledDate).getTime() >= now && !['completed', 'cancelled', 'candidate_declined', 'employer_cancelled', 'no_show'].includes(item.status));
    const today = upcoming.filter((item) => new Date(item.scheduledDate).toDateString() === new Date().toDateString());
    const pendingFeedback = interviews.filter((item) => item.status === 'completed' && !item.resultStatus && !item.notes);
    return {
      total: interviews.length,
      upcoming: upcoming.length,
      today: today.length,
      pendingFeedback: pendingFeedback.length,
      completed: interviews.filter((item) => item.status === 'completed').length
    };
  }, [interviews, now]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return interviews
      .filter((item) => {
        const scheduledTime = new Date(item.scheduledDate).getTime();
        const closed = ['completed', 'cancelled', 'candidate_declined', 'employer_cancelled', 'no_show'].includes(item.status);
        if (activeTab === 'upcoming') return scheduledTime >= now && !closed;
        if (activeTab === 'pending_feedback') return item.status === 'completed' && !item.resultStatus && !item.notes;
        if (activeTab === 'past') return scheduledTime < now || closed;
        return true;
      })
      .filter((item) => !typeFilter || item.type === typeFilter)
      .filter((item) => {
        if (!query) return true;
        return [
          item.candidate?.name,
          item.candidate?.email,
          item.job?.title,
          item.title,
          item.platform,
          item.status
        ].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
      })
      .sort((a, b) => {
        const aTime = new Date(a.scheduledDate).getTime();
        const bTime = new Date(b.scheduledDate).getTime();
        return activeTab === 'past' ? bTime - aTime : aTime - bTime;
      });
  }, [activeTab, interviews, now, search, typeFilter]);

  const handleJoin = (interview) => {
    window.open(meetingHref(interview.meetingLink), '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async (interview) => {
    await navigator.clipboard.writeText(interview.meetingLink);
    toast.success('Meeting link copied.');
  };

  const handleConfirm = async (interview) => {
    setBusyKey(`${interview.id}:confirm`);
    try {
      await interviewAPI.confirm(interview.id);
      toast.success('Interview confirmation recorded.');
      loadInterviews();
    } catch (error) {
      toast.error(extractError(error, 'Could not confirm interview'));
    } finally {
      setBusyKey('');
    }
  };

  const handleCancel = async (interview) => {
    const reason = window.prompt('Cancellation reason');
    if (reason === null) return;
    setBusyKey(`${interview.id}:cancel`);
    try {
      await interviewAPI.cancel(interview.id, reason);
      toast.success('Interview cancelled.');
      loadInterviews();
    } catch (error) {
      toast.error(extractError(error, 'Could not cancel interview'));
    } finally {
      setBusyKey('');
    }
  };

  if (loading && interviews.length === 0) {
    return <InterviewsOverviewSkeleton />;
  }

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
      <style>{`
        @keyframes shimmer-sweep {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes subtle-pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.25); }
          50% { box-shadow: 0 0 0 3px rgba(0, 0, 0, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
        }
        @keyframes subtle-pulse-ring-emerald {
          0% { box-shadow: 0 0 0 0 rgba(5, 150, 105, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(5, 150, 105, 0); }
          100% { box-shadow: 0 0 0 0 rgba(5, 150, 105, 0); }
        }
        @keyframes subtle-pulse-ring-sky {
          0% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(2, 132, 199, 0); }
          100% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0); }
        }
        @keyframes subtle-pulse-ring-amber {
          0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(245, 158, 11, 0); }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
        }
        .btn-premium-base {
          position: relative;
          overflow: hidden;
        }
        .btn-premium-base::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(110deg, transparent 25%, rgba(255, 255, 255, 0.4) 50%, transparent 75%);
          background-size: 200% 100%;
          animation: shimmer-sweep 2s infinite linear;
          opacity: 0;
          transition: opacity 1s ease-in-out;
          pointer-events: none;
        }
        .btn-premium-highlight::after {
          opacity: 1;
        }
        .btn-premium-highlight {
          animation: subtle-pulse-ring 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        .btn-premium-highlight-emerald {
          animation: subtle-pulse-ring-emerald 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        .btn-premium-highlight-sky {
          animation: subtle-pulse-ring-sky 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        .btn-premium-highlight-amber {
          animation: subtle-pulse-ring-amber 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Interviews</p>
            <h1 className="mt-2 font-display text-3xl font-normal tracking-tight text-[#0b1f3a]">Interview Workflow</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Coordinate mutual-interest interviews, track confirmations, and store structured feedback against the job match.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button 
              onClick={() => navigate('/interviews/schedule')} 
              className={`inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-lg px-4 text-sm font-medium text-white btn-premium-base transition-colors duration-1000 ${
                isHighlighting
                  ? 'bg-black btn-premium-highlight'
                  : 'bg-[#0b1f3a] hover:bg-[#12345c]'
              }`}
            >
              <PiPlusLight size={13} /> Schedule
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
            <PiWarningCircleLight size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <Metric icon={PiCalendarCheckLight} label="Upcoming" value={formatNumber(metrics.upcoming)} detail="Open schedule" tone="sky" />
          <Metric icon={PiVideoCameraLight} label="Today" value={formatNumber(metrics.today)} detail="Same-day" tone="green" />
          <Metric icon={PiChatCircleLight} label="Needs feedback" value={formatNumber(metrics.pendingFeedback)} detail="Completed" tone="amber" />
          <Metric icon={PiClipboardTextLight} label="Completed" value={formatNumber(metrics.completed)} detail="All time" tone="slate" />
          <Metric icon={PiUsersLight} label="Total" value={formatNumber(metrics.total)} detail="Employer scope" tone="slate" />
        </div>

        <section className="rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="inline-flex overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-1">
              {[
                ['upcoming', 'Upcoming'],
                ['pending_feedback', 'Needs feedback'],
                ['past', 'Past'],
                ['all', 'All']
              ].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`h-8 whitespace-nowrap rounded-md px-3 text-xs font-medium ${activeTab === id ? 'bg-white text-[#0b1f3a] shadow-sm' : 'text-slate-500'}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex flex-1 flex-col gap-3 sm:flex-row xl:max-w-xl">
              <div className="relative min-w-0 flex-1">
                <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                <input value={search} onChange={(event) => setSearch(event.target.value)} className={`${inputClass} w-full pl-9`} placeholder="Search interviews" />
              </div>
              <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} className={inputClass}>
                <option value="">All formats</option>
                <option value="video_call">Video</option>
                <option value="phone">Phone</option>
                <option value="in_person">In person</option>
              </select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-4">
              <EmptyState title="No interviews found" copy="Schedule interviews from mutual-interest matches or adjust filters to include more statuses." />
            </div>
          ) : (
            <div className="overflow-x-auto px-4">
              <table className="w-full min-w-[1120px] text-left">
                <thead>
                  <tr className="border-b border-slate-100 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                    <th className="py-3 pr-4">Candidate</th>
                    <th className="py-3 pr-4">Interview</th>
                    <th className="py-3 pr-4">Time</th>
                    <th className="py-3 pr-4">Platform</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((interview) => (
                    <InterviewRow
                      key={interview.id}
                      interview={interview}
                      busy={busyKey.startsWith(`${interview.id}:`) ? busyKey.split(':')[1] : ''}
                      onJoin={handleJoin}
                      onCopy={handleCopy}
                      onConfirm={handleConfirm}
                      onCancel={handleCancel}
                      onFeedback={setFeedbackInterview}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {feedbackInterview && (
        <FeedbackModal
          interview={feedbackInterview}
          onClose={() => setFeedbackInterview(null)}
          onSaved={loadInterviews}
        />
      )}
    </div>
  );
};

export default InterviewsOverview;
